<template>
  <j-modal
    :title="title"
    :width="width"
    :visible="visible"
    :maskClosable="false"
    :forceRender="true"
    :style="modalStyle"
    fullscreen
    switchFullscreen
    @cancel="handleCancel"
    wrapClassName="ant-modal-cust-warp">
    
    <!-- 顶部工具栏 - 仅在预览模式显示 -->
    <div v-if="!designerVisible" class="top-toolbar">
      <div class="toolbar-left" style="display:flex; align-items:center; gap:8px;">
        <a-button key="templateSetting" icon="setting" :disabled="autoPrint" @click="handleTemplateSetting">模板设置</a-button>
        <a-button key="printPreview" type="primary" icon="printer" @click="handlePrintPreview">打印预览</a-button>
        <a-button key="exportPdf" icon="file-pdf" @click="handleExportPdf">导出PDF</a-button>
        <a-checkbox v-model="autoPrint" @change="onAutoPrintChange">自动</a-checkbox>
      </div>
    </div>
    
    <template slot="footer">
      <div class="footer-bar">
        <div class="footer-right">
          <a-button @click="handleCancel">取消</a-button>
        </div>
      </div>
    </template>
    
    <!-- 预览模式 -->
    <a-spin v-if="!designerVisible" :spinning="loading" tip="正在加载模板...">
      <div class="hiprint-container">
        <div v-if="!templateJson && !loading" class="no-template">
          <a-empty description="暂无打印模板">
            <a-button type="primary" @click="handleTemplateSetting">去设置模板</a-button>
          </a-empty>
        </div>
        <div v-else class="preview-wrapper">
          <div 
            ref="printContent" 
            class="print-content"
            :style="{ transform: `scale(${zoomLevel / 100})`, transformOrigin: 'top center' }">
          </div>
        </div>
      </div>
    </a-spin>
    
    <!-- 设计器模式 - 直接嵌入当前模态框 -->
    <div v-if="designerVisible" class="designer-wrapper">
      <EmbeddedDesigner
        ref="embeddedDesigner"
        :billType="billTypeCode"
        :billTypeName="billType"
        :billNo="currentBillNo"
        :templateId="selectedTemplateId"
        :billData="printData"
        @close="onDesignerClose"
        @saved="onDesignerSaved"
      />
    </div>
    
    <!-- 内嵌打印对话框 -->
    <a-modal
      :visible="printDialogVisible"
      title="打印预览"
      width="90%"
      :footer="null"
      @cancel="printDialogVisible = false"
      wrapClassName="print-dialog-modal">
      <div class="print-dialog-container">
        <!-- 左侧：打印设置 -->
        <div class="print-settings">
          <div class="settings-section">
            <h4>打印机</h4>
            <a-select v-model="selectedPrinter" placeholder="选择打印机" style="width: 100%;">
              <a-select-option v-for="printer in printerList" :key="printer">{{ printer }}</a-select-option>
            </a-select>
            <a-button size="small" icon="reload" style="margin-top: 8px;" @click="refreshPrinters">刷新</a-button>
          </div>
          
          <div class="settings-section">
            <h4>份数</h4>
            <a-input-number v-model="printCopies" :min="1" :max="99" style="width: 100%;" />
          </div>
          
          <div class="settings-section">
            <h4>方向</h4>
            <a-radio-group v-model="printOrientation">
              <a-radio value="portrait">纵向</a-radio>
              <a-radio value="landscape">横向</a-radio>
            </a-radio-group>
          </div>
          
          <div class="settings-section">
            <h4>纸张</h4>
            <a-select defaultValue="A4" style="width: 100%;">
              <a-select-option value="A4">A4</a-select-option>
              <a-select-option value="A5">A5</a-select-option>
              <a-select-option value="B5">B5</a-select-option>
              <a-select-option value="Letter">Letter</a-select-option>
            </a-select>
          </div>
          
          <div class="print-actions">
            <a-button type="primary" icon="printer" block @click="doPrint">打印</a-button>
          </div>
        </div>
        
        <!-- 右侧：预览区域 -->
        <div class="print-preview-area">
          <div class="preview-toolbar">
            <span>共 1 页</span>
          </div>
          <div class="preview-content" ref="printPreviewContent">
            <div 
              class="preview-paper"
              :style="{ transform: `scale(${zoomLevel / 100})`, transformOrigin: 'top center' }">
              <div v-html="previewHtml"></div>
            </div>
          </div>
        </div>
      </div>
    </a-modal>
    
    </j-modal>
</template>

<script>
import { mixinDevice } from '@/utils/mixin'
import { 
  getList, 
  getDefaultTemplateByBillType,
  getDepotItemDetailList,
  getDepotHeadByNumber,
  getCurrentSystemInfo,
  loadTemplateWithFallback
} from '@/api/plugin/print-template'
import HiprintService from '@/services/hiprint-service'
import { registerAllFieldTypes, getBillCodeByName } from '@/services/hiprint-register'
import { getBillTypeName, getDefaultTemplateByCode, getDefaultTemplateByName } from '@/assets/print_template'
import { getBillTypeFromNo, normalizeBillType } from '@/utils/billTypeUtils'
import { moneyToShort } from '@/utils/moneyUtils'
import EmbeddedDesigner from '@/views/plugin/print-template/EmbeddedDesigner.vue'

// 单据类型映射 - 使用短代码 (与TemplateDesignerV2兼容)
// 注意：所有"其他"统一使用"其它"
const BILL_TYPE_MAP = {
  '请购单': 'QGD',
  '采购订单': 'CGDD',
  '采购入库': 'CGRK',
  '采购退货': 'CGTH',
  '采购退货出库': 'CGTH',
  '采购退货入库': 'CGTH',
  '销售订单': 'XSDD',
  '销售出库': 'XSCK',
  '销售退货': 'XSTH',
  '销售退货入库': 'XSTH',
  '销售退货出库': 'XSTH',
  '零售出库': 'LSCK',
  '零售退货': 'LSTH',
  '零售退货入库': 'LSTH',
  '其它入库': 'QTRK',
  '其它出库': 'QTCK',
  '组装单': 'ZZD',
  '拆卸单': 'CXD',
  '调拔出库': 'DBCK',
  '调拨出库': 'DBCK',
  '盘点录入': 'PDLR',
  '盘点复盘': 'PDFP',
  '资产申领': 'NSLD',
  '资产申领其它': 'NSLD',
  '资产出库': 'NCKD'
}

// billType代码到显示名称的映射表 - 用于单据抬头
const BILL_TYPE_TO_DISPLAY_NAME = {
  'QTRK': '其它入库单',
  'QTCK': '其它出库单',
  'CXD': '拆卸单',
  'ZZD': '组装单',
  'QGD': '请购单',
  'DBCK': '调拔出库',
  'CGRK': '采购入库单',
  'CGDD': '采购订单',
  'CGTH': '采购退货单',
  'XSCK': '销售出库单',
  'XSDD': '销售订单',
  'XSTH': '销售退货单',
  'LSCK': '零售出库单',
  'LSTH': '零售退货单',
  'PDLR': '盘点录入单',
  'PDFP': '盘点复盘单'
}

export default {
  name: 'BillPrintHiprint',
  mixins: [mixinDevice],
  components: {
    EmbeddedDesigner
  },
  data() {
    return {
      title: '三联打印预览',
      width: '1500px',
      visible: false,
      modalStyle: '',
      billPrintUrl: '',
      height: '',
      model: {},
      loading: false,
      billType: '',
      billTypeCode: '',
      templateJson: null,
      hiprintTemplate: null,
      printData: null,
      
      // 模板列表
      templateList: [],
      selectedTemplateId: null,
      
      // 缩放
      zoomLevel: 100,
      
      // 当前单据
      currentBillNo: null,
      currentBillType: null,
      
      // 新增：自动打印开关
      autoPrint: false,
      // 系统全局自动打印开关
      systemAutoPrint: false,
      
      // 新增：模板设计器弹窗
      designerVisible: false,
      
      // 新增：打印预览对话框
      printDialogVisible: false,
      printerList: [],
      selectedPrinter: '',
      printCopies: 1,
      printOrientation: 'portrait',
      previewHtml: '',
      
      // 数据缓存
      dataCache: {},
      CACHE_TTL: 5 * 60 * 1000 // 5分钟缓存有效期
    }
  },
  methods: {
    /**
     * 显示打印窗口
     */
    async show(record, billPrintUrl, billPrintHeight, billType) {
      this.height = billPrintHeight || '700px'
      this.billPrintUrl = billPrintUrl
      this.visible = true
      this.modalStyle = 'top:20px;height: 95%;'
      this.model = Object.assign({}, record)
      this.loading = true
      
      // 获取单据编号
      const billNo = record.number || record.billNo || record.no
      
      // ========== 确定单据类型 ==========
      let finalBillType = billType
      let finalBillTypeCode = ''
      
      // 1. 如果传入了 billType（可能是中文名称或代码）
      if (finalBillType) {
        // 尝试从映射表查找
        finalBillTypeCode = BILL_TYPE_MAP[finalBillType] || finalBillType
        // 再次规范化（处理中英文代码）
        finalBillTypeCode = normalizeBillType(finalBillTypeCode)
      }
      
      // 2. 如果没有识别出，从单据编号提取
      if (!finalBillTypeCode && billNo) {
        finalBillTypeCode = getBillTypeFromNo(billNo)
        if (finalBillTypeCode) {
          finalBillType = getBillTypeName(finalBillTypeCode)
        }
      }
      
      // 3. 如果都没有，使用默认值
      if (!finalBillTypeCode) {
        finalBillTypeCode = 'QGD' // 默认使用请购单
        finalBillType = getBillTypeName(finalBillTypeCode)
      }
      
      console.log('[BillPrintHiprint] show: billNo=', billNo, 'billType=', finalBillType, 'billTypeCode=', finalBillTypeCode)
      
      // 设置类型
      this.billType = finalBillType
      this.billTypeCode = finalBillTypeCode
      
      // 保存当前单据信息
      this.currentBillNo = billNo
      this.currentBillType = finalBillTypeCode
      
      try {
        await this.loadData()
        await this.loadTemplateList()
        await this.loadAndRender()
      } catch (error) {
        console.error('加载打印数据失败:', error)
        this.templateJson = this.getFallbackTemplate()
        this.$nextTick(() => {
          this.renderTemplate()
        })
      } finally {
        this.loading = false
      }
      
      this.$nextTick(() => {
        // 全局自动打印（优先级最高）
        if (this.systemAutoPrint) {
          setTimeout(() => {
            this.handlePrintPreview()
          }, 1000)
        }
        // 本地自动打印
        else if (this.autoPrint) {
          setTimeout(() => {
            this.handlePrintPreview()
          }, 1000)
        }
      })
    },
    
    /**
     * 生成缓存键
     */
    getCacheKey(billType, billNo) {
      return `${billType}_${billNo}`
    },
    
    /**
     * 获取缓存数据
     */
    getCachedData(billType, billNo) {
      const cacheKey = this.getCacheKey(billType, billNo)
      const cached = this.dataCache[cacheKey]
      
      if (cached && cached.timestamp && (Date.now() - cached.timestamp < this.CACHE_TTL)) {
        console.log('[Cache] 命中缓存:', cacheKey)
        return cached.data
      }
      
      // 缓存过期或不存在
      if (cached) {
        console.log('[Cache] 缓存过期:', cacheKey)
        delete this.dataCache[cacheKey]
      }
      return null
    },
    
    /**
     * 设置缓存数据
     */
    setCachedData(billType, billNo, data) {
      const cacheKey = this.getCacheKey(billType, billNo)
      this.dataCache[cacheKey] = {
        data: data,
        timestamp: Date.now()
      }
      console.log('[Cache] 设置缓存:', cacheKey)
    },
    
    /**
     * 清除缓存
     */
    clearCache(billType, billNo) {
      const cacheKey = this.getCacheKey(billType, billNo)
      if (this.dataCache[cacheKey]) {
        delete this.dataCache[cacheKey]
        console.log('[Cache] 清除缓存:', cacheKey)
      }
    },
    
    /**
     * 获取单据类型代码
     */
    getBillTypeCode(billType) {
      return BILL_TYPE_MAP[billType] || billType
    },
    
    /**
     * 加载模板列表
     */
    async loadTemplateList() {
      if (!this.billTypeCode) return
      
      try {
        // 直接使用 billType code 查询后端（如 CGDD）
        const res = await getList({ billType: this.billTypeCode, size: 100 })
        if (res.code === 200 && res.data) {
          this.templateList = res.data.list || res.data || []
          
          // 查找默认模板
          const defaultTemplate = this.templateList.find(t => t.isDefault === 1 || t.isDefault === true)
          if (defaultTemplate) {
            this.selectedTemplateId = defaultTemplate.id
          } else if (this.templateList.length > 0) {
            this.selectedTemplateId = this.templateList[0].id
          }
        }
      } catch (error) {
        console.warn('加载模板列表失败:', error)
        this.templateList = []
      }
    },
    
    /**
     * 加载并渲染模板
     */
    async loadAndRender() {
      try {
        if (this.selectedTemplateId) {
          const selectedTemplate = this.templateList.find(t => t.id === this.selectedTemplateId)
          if (selectedTemplate && selectedTemplate.templateJson) {
            this.templateJson = this.parseTemplateJson(selectedTemplate.templateJson)
          } else {
            await this.loadDefaultTemplate()
          }
        } else {
          await this.loadDefaultTemplate()
        }
        
        this.$nextTick(() => {
          this.renderTemplate()
        })
      } catch (error) {
        console.error('加载并渲染模板失败:', error)
      }
    },
    
    /**
     * 加载默认模板（使用两级回退）
     */
    async loadDefaultTemplate() {
      if (!this.billTypeCode) return
      
      this.loading = true
      console.log('[BillPrintHiprint] loadDefaultTemplate, billTypeCode:', this.billTypeCode)
      
      try {
        // 使用新的两级回退加载函数
        const userInfo = this.$store && (this.$store.getters.userInfo || (this.$store.state.user && this.$store.state.user.info))
        const tenantId = userInfo ? userInfo.tenantId : null
        const result = await loadTemplateWithFallback(this.billTypeCode, tenantId)
        
        if (result && result.templateJson && result.templateJson.panels) {
          console.log('[BillPrintHiprint] 使用两级回退加载的模板')
          this.templateJson = result.templateJson
        } else {
          console.log('[BillPrintHiprint] 两级回退未找到模板，使用备用')
          this.templateJson = this.getFallbackTemplate()
        }
      } catch (error) {
        console.warn('[BillPrintHiprint] loadDefaultTemplate error:', error)
        this.templateJson = this.getFallbackTemplate()
      } finally {
        this.loading = false
      }
    },
    
    /**
     * 解析模板 JSON
     */
    parseTemplateJson(templateJson) {
      if (!templateJson) return null
      
      try {
        let json = templateJson
        if (typeof json === 'string') {
          json = JSON.parse(json)
        }
        return json
      } catch (e) {
        console.error('解析模板 JSON 失败:', e)
        return null
      }
    },
    
    /**
     * 获取备用模板
     */
    getFallbackTemplate() {
      return {
        panels: [{
          index: 0,
          name: 1,
          width: 210,
          height: 297,
          printElements: [
            {
              tid: 'provider',
              options: {
                left: 60, top: 10, width: 90, height: 15,
                field: 'billTitle', title: 'billTitle',
                fontSize: 16, fontWeight: 700, textAlign: 'center', hideTitle: true
              }
            },
            {
              tid: 'provider',
              options: {
                left: 20, top: 30, width: 80, height: 8,
                field: 'number', title: '单据编号',
                fontSize: 10, textAlign: 'left', hideTitle: false
              }
            },
            {
              tid: 'provider',
              options: {
                left: 120, top: 30, width: 80, height: 8,
                field: 'operTimeStr', title: '单据日期',
                fontSize: 10, textAlign: 'left', hideTitle: false
              }
            },
            {
              tid: 'provider',
              options: {
                left: 20, top: 45, width: 80, height: 8,
                field: 'organName', title: '往来单位',
                fontSize: 10, textAlign: 'left', hideTitle: false
              }
            },
            {
              tid: 'provider',
              options: {
                left: 120, top: 45, width: 80, height: 8,
                field: 'depotName', title: '仓库',
                fontSize: 10, textAlign: 'left', hideTitle: false
              }
            },
            {
              tid: 'table',
              options: {
                left: 20, top: 60, width: 170, height: 50,
                field: 'table', title: '明细',
                columns: [
                  { title: '序号', field: 'rowIndex', width: 12, align: 'center' },
                  { title: '商品名称', field: 'name', width: 40, align: 'left' },
                  { title: '规格', field: 'standard', width: 25, align: 'left' },
                  { title: '单位', field: 'unit', width: 12, align: 'center' },
                  { title: '数量', field: 'operNumber', width: 20, align: 'right' },
                  { title: '单价', field: 'unitPrice', width: 20, align: 'right' },
                  { title: '金额', field: 'allPrice', width: 20, align: 'right' }
                ],
                headerHeight: 8, rowHeight: 8, showHeader: true
              }
            },
            {
              tid: 'provider',
              options: {
                left: 20, top: 120, width: 80, height: 8,
                field: 'totalQty', title: '合计数量',
                fontSize: 10, textAlign: 'left', hideTitle: false
              }
            },
            {
              tid: 'provider',
              options: {
                left: 120, top: 120, width: 80, height: 8,
                field: 'totalAmount', title: '合计金额',
                fontSize: 10, textAlign: 'left', hideTitle: false
              }
            },
            {
              tid: 'provider',
              options: {
                left: 20, top: 140, width: 80, height: 8,
                field: 'remark', title: '备注',
                fontSize: 10, textAlign: 'left', hideTitle: false
              }
            },
            {
              tid: 'provider',
              options: {
                left: 20, top: 160, width: 80, height: 8,
                field: 'creatorName', title: '制单人',
                fontSize: 10, textAlign: 'left', hideTitle: false
              }
            }
          ]
        }]
      }
    },
    
    /**
     * 加载数据 - 使用原始系统接口，两阶段并行 + 缓存
     * 第一阶段：并行获取单据主体 + 公司信息
     * 第二阶段：根据主体ID获取明细
     */
    async loadData() {
      const billNo = this.model.number || this.model.billNo || this.model.no
      if (!billNo || !this.billTypeCode) return
      
      // 尝试从缓存获取
      const cachedData = this.getCachedData(this.billTypeCode, billNo)
      if (cachedData) {
        this.printData = cachedData
        return
      }
      
      this.loading = true
      
      try {
        // 第一阶段：并行获取单据主体和公司信息
        console.log('[loadData] 第一阶段：并行获取主体和公司信息', billNo)
        const [headRes, systemRes] = await Promise.all([
          getDepotHeadByNumber(billNo),
          getCurrentSystemInfo()
        ])
        
        console.log('[loadData] 第一阶段完成', { headRes, systemRes })
        
        if (!headRes || headRes.code !== 200 || !headRes.data) {
          console.warn('[loadData] 单据主体获取失败')
          this.printData = this.transformData(this.model, [])
          return
        }
        
        const billData = headRes.data
        const headId = billData.id
        
        // 第二阶段：获取单据明细
        let detailRows = []
        if (headId) {
          console.log('[loadData] 第二阶段：获取单据明细', headId)
          const detailRes = await getDepotItemDetailList({
            headerId: headId,
            mpList: '扩展1,扩展2,扩展3'
          })
          
          console.log('[loadData] 第二阶段完成', detailRes)
          
          if (detailRes && detailRes.code === 200 && detailRes.data) {
            detailRows = detailRes.data.rows || []
          }
        }
        
        // 合并数据
        const companyData = (systemRes && systemRes.code === 200) ? systemRes.data : {}
        
        // 读取全局自动打印配置，同步到本地 autoPrint
        if (companyData.autoPrint !== undefined && companyData.autoPrint !== null) {
          this.autoPrint = (companyData.autoPrint === '1')
          this.systemAutoPrint = (companyData.autoPrint === '1')
        }
        
        const rawData = {
          ...billData,
          company: companyData,
          detailList: detailRows
        }
        
        // 转换数据并缓存
        this.printData = this.transformData(rawData)
        this.setCachedData(this.billTypeCode, billNo, this.printData)
        
        console.log('[loadData] 数据加载完成', {
          billData: billData,
          companyData: companyData,
          detailCount: detailRows.length,
          finalData: this.printData
        })
        
      } catch (error) {
        console.error('[loadData] 加载数据失败:', error)
        // 降级处理
        this.printData = this.transformData(this.model, [])
      } finally {
        this.loading = false
      }
    },
    
    /**
     * 转换数据格式 - 直接映射原始API字段
     * @param {Object} data - 原始数据（包含主体、公司、明细）
     * @param {Array} [detailList] - 明细列表（可选，优先使用data.detailList）
     */
    transformData(data, detailList) {
      if (!data) data = {}
      
      // 获取明细数据
      const detailRows = data.detailList || detailList || []
      
      // 公司信息
      const company = data.company || {}
      
      // 确定是采购还是销售类型（用于设置往来单位别名）
      const isPurchase = this.billTypeCode === 'CGDD' || this.billTypeCode === 'CGRK' || 
                         this.billTypeCode === 'QGD' || this.billTypeCode === 'CGTH'
      const isSales = this.billTypeCode === 'XSDD' || this.billTypeCode === 'XSCK' || 
                     this.billTypeCode === 'XSTH'
      
      // 构建单据标题：使用 billType 对应的显示名称
      const displayName = BILL_TYPE_TO_DISPLAY_NAME[this.billTypeCode] || 
                          data.templateName || data.depotHeadType || data.subType || this.billType || '单据'
      const billTitle = displayName
      
      // 往来单位名称 - 根据单据类型设置别名
      const organName = data.organName || ''
      const supplierName = isPurchase ? organName : ''
      const customerName = isSales ? organName : ''
      
      // ========== 分离明细行和合计行 ==========
      // 最后一行如果没有 name 和 barCode 字段则认为是合计行
      const isSummaryRow = (row) => row && !row.name && !row.barCode && row.allPrice !== undefined
      let summaryRow = null
      let cleanDetailRows = detailRows
      
      // 检查最后一行是否为合计行
      if (detailRows.length > 0 && isSummaryRow(detailRows[detailRows.length - 1])) {
        summaryRow = detailRows[detailRows.length - 1]
        cleanDetailRows = detailRows.slice(0, -1)  // 去掉最后一行合计
        console.log('[transformData] 检测到合计行，已分离', summaryRow)
      }
      
      // ========== 计算金额字段（参考原始 billPrint 实现）==========
      // 根据单据类型判断使用哪个字段计算金额
      // 原始代码：billType 为以下类型时使用 totalPrice，否则使用 discountLastMoney + discountMoney
      // 注意：原始系统的 billType 对应 data.subType 字段
      const billTypeName = data.subType || data.depotHeadType || data.type || this.billType || ''
      console.log('[transformData] 原始数据检查:', {
        subType: data.subType,
        depotHeadType: data.depotHeadType,
        type: data.type,
        totalPrice: data.totalPrice,
        discountLastMoney: data.discountLastMoney,
        discountMoney: data.discountMoney
      })
      
      // 所有使用 totalPrice 的单据类型
      const useTotalPriceTypes = [
        '零售出库', '零售退货入库', 
        '采购订单', '销售订单', 
        '其它入库', '其它出库', 
        '调拨出库', '组装单', '拆卸单',
        '请购单', '采购入库', '采购退货',
        '销售出库', '销售退货', '零售入库'
      ]
      
      let finalAmount = 0
      if (useTotalPriceTypes.includes(billTypeName)) {
        finalAmount = parseFloat(data.totalPrice) || 0
      } else {
        finalAmount = (parseFloat(data.discountLastMoney) || 0) + (parseFloat(data.discountMoney) || 0)
      }
      
      // 如果两种方式都得到0，尝试从明细计算
      if (finalAmount === 0 && cleanDetailRows.length > 0) {
        finalAmount = cleanDetailRows.reduce((sum, item) => {
          const itemPrice = parseFloat(item.allPrice) || 0
          return sum + itemPrice
        }, 0)
        console.log('[transformData] 从明细计算金额:', finalAmount)
      }
      
      // 调用 moneyToShort（即 toCnMoney）将金额转换为大写
      const cnMoney = data.billDetailCnMoney || moneyToShort(finalAmount)
      
      // 计算汇总数据
      const totalQty = summaryRow ? (parseFloat(summaryRow.operNumber) || 0) : 
                       cleanDetailRows.reduce((sum, item) => sum + (parseFloat(item.operNumber) || 0), 0)
      const totalAmount = summaryRow ? (parseFloat(summaryRow.allPrice) || 0) : 
                          cleanDetailRows.reduce((sum, item) => sum + (parseFloat(item.allPrice) || 0), 0)
      
      console.log('[transformData] 金额计算:', {
        billTypeName,
        useTotalPrice: useTotalPriceTypes.includes(billTypeName),
        finalAmount,
        cnMoney
      })
      
      // 返回转换后的数据 - 所有值已预先计算好
      return {
        // ========== 单据基本信息 ==========
        billTitle: billTitle,
        number: data.number || data.billNo || '',
        billNo: data.number || data.billNo || '',
        operTimeStr: data.operTimeStr || '',
        billDate: data.operTimeStr || '',
        createTime: data.createTime || '',
        billType: data.type || this.billType || '',
        subType: data.subType || '',
        depotHeadType: data.depotHeadType || '',
        
        // ========== 公司信息 ==========
        companyName: company.companyName || '',
        companyAddress: company.companyAddress || '',
        companyTel: company.companyTel || '',
        companyFax: company.companyFax || '',
        companyContacts: company.companyContacts || '',
        companyPostCode: company.companyPostCode || '',
        
        // ========== 往来单位信息 ==========
        organName: organName,
        supplierName: supplierName,
        customerName: customerName,
        contacts: data.contacts || '',
        telephone: data.telephone || '',
        address: data.address || '',
        organType: data.organType || '',
        
        // ========== 账户/财务信息 ==========
        accountId: data.accountId || '',
        accountName: data.accountName || '',
        payType: data.payType || '',
        changeAmount: data.changeAmount || 0,
        backAmount: data.backAmount || 0,
        totalPrice: data.totalPrice || 0,
        totalPriceStr: data.totalPriceStr || '',
        discount: data.discount || 0,
        discountMoney: data.discountMoney || 0,
        discountLastPrice: data.discountLastPrice || 0,
        discountLastMoney: data.discountLastMoney || 0,
        otherMoney: data.otherMoney || 0,
        deposit: data.deposit || 0,
        lastDeposit: data.lastDeposit || 0,
        debt: data.debt || 0,
        lastDebt: data.lastDebt || 0,
        
        // ========== 计算好的金额字段 ==========
        billDetailAllPrice: finalAmount,
        billDetailCnMoney: cnMoney,
        
        // ========== 其他信息 ==========
        remark: data.remark || '',
        status: data.status || '',
        statusStr: data.statusStr || '',
        linkNumber: data.linkNumber || '',
        materialsList: data.materialsList || '',
        projectName: data.projectName || '',
        bankName: data.bankName || '',
        accountNumber: data.accountNumber || '',
        taxNum: data.taxNum || '',
        phoneNum: data.phoneNum || '',
        
        // ========== 明细数据（已过滤合计行）==========
        // 使用多个别名以兼容不同模板
        detailList: cleanDetailRows,
        table: cleanDetailRows,
        itemList: cleanDetailRows,
        
        // ========== 汇总数据（已计算）==========
        totalQty: totalQty,
        totalAmount: totalAmount
      }
    },
    
    /**
     * 渲染模板
     */
    renderTemplate() {
      if (!this.templateJson) {
        console.warn('renderTemplate: templateJson is null')
        return
      }
      if (!this.$refs.printContent) {
        console.warn('renderTemplate: printContent ref not ready')
        return
      }
      
      try {
        if (HiprintService.isAvailable()) {
          const html = HiprintService.renderHtml(this.templateJson, this.printData || {})
          if (html && html.trim()) {
            this.$refs.printContent.innerHTML = html
          } else {
            console.warn('hiprint渲染返回空，使用降级方案')
            this.renderFallback()
          }
        } else {
          console.warn('hiprint不可用，使用降级方案')
          this.renderFallback()
        }
      } catch (error) {
        console.error('渲染模板失败:', error)
        this.renderFallback()
      }
    },
    
    /**
     * 降级渲染
     */
    renderFallback() {
      if (this.$refs.printContent && this.printData) {
        this.$refs.printContent.innerHTML = this.generateSimpleHTML()
      }
    },
    
    /**
     * 生成简单 HTML（备用方案）
     */
    generateSimpleHTML() {
      const d = this.printData || {}
      const itemList = d.itemList || []
      const counterparty = d.supplierName || d.customerName || d.organName || ''
      return `
        <div style="padding: 20px; font-family: SimSun;">
          <div style="text-align: center; margin-bottom: 20px;">
            <h1 style="font-size: 24px; margin: 0;">${d.companyName || ''}</h1>
            <h2 style="font-size: 20px; margin: 10px 0; color: #1890ff;">${d.billTitle || '单据'}</h2>
          </div>
          <div style="margin-bottom: 15px; display: flex; justify-content: space-between;">
            <div>
              <span style="margin-right: 15px;">单据编号：${d.billNo || ''}</span>
              <span>单据日期：${d.billDate || ''}</span>
            </div>
            <div>
              <span style="margin-right: 15px;">${d.supplierName ? '供应商' : (d.customerName ? '客户' : '往来单位')}：${counterparty}</span>
              <span>仓库：${d.storeName || d.warehouseName || ''}</span>
            </div>
          </div>
          <table style="width: 100%; border-collapse: collapse; margin-bottom: 15px;">
            <thead>
              <tr style="background: #f0f0f0;">
                <th style="border: 1px solid #d9d9d9; padding: 8px; width: 40px;">序号</th>
                <th style="border: 1px solid #d9d9d9; padding: 8px;">商品名称</th>
                <th style="border: 1px solid #d9d9d9; padding: 8px; width: 80px;">规格</th>
                <th style="border: 1px solid #d9d9d9; padding: 8px; width: 50px;">单位</th>
                <th style="border: 1px solid #d9d9d9; padding: 8px; width: 70px; text-align: right;">数量</th>
                <th style="border: 1px solid #d9d9d9; padding: 8px; width: 70px; text-align: right;">单价</th>
                <th style="border: 1px solid #d9d9d9; padding: 8px; width: 80px; text-align: right;">金额</th>
              </tr>
            </thead>
            <tbody>
              ${itemList.map(item => `
                <tr>
                  <td style="border: 1px solid #d9d9d9; padding: 8px; text-align: center;">${item.rowIndex || ''}</td>
                  <td style="border: 1px solid #d9d9d9; padding: 8px;">${item.itemName || item.name || ''}</td>
                  <td style="border: 1px solid #d9d9d9; padding: 8px;">${item.spec || item.standard || ''}</td>
                  <td style="border: 1px solid #d9d9d9; padding: 8px;">${item.unit || ''}</td>
                  <td style="border: 1px solid #d9d9d9; padding: 8px; text-align: right;">${item.qty || item.operNumber || 0}</td>
                  <td style="border: 1px solid #d9d9d9; padding: 8px; text-align: right;">${item.price || 0}</td>
                  <td style="border: 1px solid #d9d9d9; padding: 8px; text-align: right;">${item.amount || item.money || 0}</td>
                </tr>
              `).join('')}
            </tbody>
            <tfoot>
              <tr style="background: #f5f5f5; font-weight: bold;">
                <td colspan="4" style="border: 1px solid #d9d9d9; padding: 8px; text-align: right;">合计</td>
                <td style="border: 1px solid #d9d9d9; padding: 8px; text-align: right;">${d.totalQty || 0}</td>
                <td style="border: 1px solid #d9d9d9; padding: 8px;"></td>
                <td style="border: 1px solid #d9d9d9; padding: 8px; text-align: right;">${d.totalAmount || 0}</td>
              </tr>
            </tfoot>
          </table>
          <div style="background: #f5f5f5; padding: 10px; border-radius: 4px; margin-bottom: 15px;">
            <strong>备注：</strong>${d.remark || ''}
          </div>
          <div style="display: flex; justify-content: space-between; margin-top: 30px;">
            <div>制单人：${d.operator || ''}</div>
            <div>审核人：${d.auditor || ''}</div>
          </div>
        </div>
      `
    },
    
    /**
     * 模板变更
     */
    async onTemplateChange() {
      await this.loadAndRender()
    },
    
    /**
     * 缩放变更
     */
    onZoomChange() {
      // 缩放由 CSS transform 自动处理
    },
    
    /**
     * 刷新 - 清除缓存并重新加载数据
     */
    async handleRefresh() {
      // 清除当前单据的缓存
      if (this.billTypeCode && this.currentBillNo) {
        this.clearCache(this.billTypeCode, this.currentBillNo)
      }
      // 重新加载数据
      await this.loadData()
      await this.loadAndRender()
      this.$message.success('刷新成功')
    },
    
    /**
     * 打印
     */
    handlePrint() {
      if (this.templateJson && HiprintService.isAvailable()) {
        try {
          HiprintService.print(this.templateJson, this.printData || {})
        } catch (error) {
          console.error('打印失败:', error)
          this.printFallback()
        }
      } else {
        this.printFallback()
      }
    },
    
    /**
     * 导出 PDF
     */
    handleExportPdf() {
      if (!this.templateJson) {
        this.$message.warning('暂无模板数据')
        return
      }
      try {
        const hiprint = window.hiprint || (window.Vue && window.Vue.prototype.$hiprint)
        if (!hiprint || !hiprint.PrintTemplate) {
          this.$message.error('打印引擎未就绪')
          return
        }
        const data = this.printData || {}
        const fileName = (this.billType || '打印模板') + '_' + new Date().toISOString().slice(0, 10)
        const tempTemplate = new hiprint.PrintTemplate({ template: this.templateJson })
        tempTemplate.toPdf(data, fileName)
        this.$message.success('PDF 导出成功')
      } catch (e) {
        console.error('PDF 导出失败:', e)
        this.$message.error('PDF 导出失败')
      }
    },
    
    /**
     * 备用打印方案
     */
    printFallback() {
      const printContent = this.$refs.printContent
      if (printContent) {
        const printWindow = window.open('', '_blank')
        printWindow.document.write(`
          <html>
            <head>
              <title>打印 - ${this.billType}</title>
              <style>
                body { margin: 0; padding: 20px; font-family: SimSun; }
                table { width: 100%; border-collapse: collapse; }
                th, td { border: 1px solid #d9d9d9; padding: 8px; }
                th { background: #f0f0f0; }
              </style>
            </head>
            <body>${printContent.innerHTML}</body>
          </html>
        `)
        printWindow.document.close()
        printWindow.print()
      }
    },
    
    /**
     * 打印预览 - 显示内嵌对话框
     */
    handlePrintPreview() {
      const printContent = this.$refs.printContent
      if (!printContent || !printContent.innerHTML) {
        this.$message.warning('暂无内容可打印')
        return
      }
      
      // 设置预览内容
      this.previewHtml = printContent.innerHTML
      
      // 获取打印机列表（模拟）
      this.printerList = this.getPrinterList()
      if (this.printerList.length > 0 && !this.selectedPrinter) {
        this.selectedPrinter = this.printerList[0]
      }
      
      // 显示对话框
      this.printDialogVisible = true
    },
    
    /**
     * 获取打印机列表（模拟）
     */
    getPrinterList() {
      return ['Microsoft Print to PDF', 'Microsoft XPS Document Writer', 'HP LaserJet Pro MFP']
    },
    
    /**
     * 刷新打印机列表
     */
    refreshPrinters() {
      this.printerList = this.getPrinterList()
      this.$message.success('打印机列表已刷新')
    },
    
    /**
     * 执行打印
     */
    doPrint() {
      const printContent = this.$refs.printContent
      if (!printContent || !printContent.innerHTML) {
        this.$message.warning('暂无内容可打印')
        return
      }
      
      // 创建打印窗口
      const printWindow = window.open('', '_blank')
      if (!printWindow) {
        this.$message.error('无法打开打印窗口，请允许弹出窗口')
        return
      }
      
      printWindow.document.write(`
        <!DOCTYPE html>
        <html>
        <head>
          <title>${this.billType || '打印'} - 打印</title>
          <style>
            body { margin: 0; padding: 20px; font-family: SimSun, 'Microsoft YaHei', sans-serif; }
            @media print {
              body { padding: 0; }
            }
          </style>
        </head>
        <body>
          ${printContent.innerHTML}
        </body>
        </html>
      `)
      printWindow.document.close()
      printWindow.focus()
      
      // 延迟执行打印
      setTimeout(() => {
        printWindow.print()
      }, 500)
      
      // 关闭对话框
      this.printDialogVisible = false
    },
    
    /**
     * 自动打印开关变化
     */
    onAutoPrintChange(checked) {
      // 全局开启时，不允许手动关闭
      if (this.systemAutoPrint && !checked) {
        this.$message.warning('系统已开启自动打印，如需关闭请到系统管理→系统配置')
        this.autoPrint = true
        return
      }
      if (checked && this.templateJson) {
        // 延迟执行确保渲染完成
        this.$nextTick(() => {
          setTimeout(() => {
            this.handlePrintPreview()
          }, 500)
        })
      }
    },
    
    /**
     * 内嵌模板设计器
     */
    handleTemplateSetting() {
      this.designerVisible = true
    },
    
    /**
     * 设计器关闭
     */
    onDesignerClose() {
      this.designerVisible = false
    },
    
    /**
     * 设计器保存 - 刷新预览
     */
    onDesignerSaved() {
      this.designerVisible = false
      this.$message.success('模板保存成功，正在刷新预览...')
      this.loadTemplateList().then(() => {
        this.loadAndRender()
      })
    },
    
    /**
     * 取消
     */
    handleCancel() {
      this.close()
    },
    
    /**
     * 关闭
     */
    close() {
      this.billPrintUrl = ''
      this.templateJson = null
      this.printData = null
      this.hiprintTemplate = null
      this.selectedTemplateId = null
      this.autoPrint = false
      this.designerVisible = false
      
      // 清除所有缓存
      this.dataCache = {}
      
      if (this.$refs.printContent) {
        this.$refs.printContent.innerHTML = ''
      }
      
      this.$emit('close')
      this.visible = false
      this.modalStyle = ''
    }
  }
}
</script>

<style scoped>
/* 顶部工具栏 */
.top-toolbar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 15px;
  background: #fafafa;
  border-bottom: 1px solid #e8e8e8;
}

/* 内容区工具栏 */
.content-toolbar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 15px;
  background: #fff;
  border-bottom: 1px solid #e8e8e8;
}

.toolbar-left {
  display: flex;
  align-items: center;
  gap: 10px;
}

.toolbar-label {
  color: #666;
  font-size: 13px;
}

.hiprint-container {
  width: 100%;
  height: calc(100vh - 280px);
  overflow: auto;
  background: #f5f5f5;
  padding: 20px;
  box-sizing: border-box;
}

.preview-wrapper {
  display: flex;
  justify-content: center;
  align-items: flex-start;
  min-height: 500px;
}

.print-content {
  background: white;
  min-width: 210mm;
  min-height: 297mm;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  transform-origin: top center;
}

.no-template {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 400px;
}

/* 底部栏 */
.footer-bar {
  display: flex;
  justify-content: flex-end;
  align-items: center;
}

.footer-right {
  display: flex;
  align-items: center;
  gap: 8px;
}

/* 设计器容器 */
.designer-wrapper {
  height: calc(100vh - 200px);
  min-height: 600px;
}

/* 打印对话框样式 */
.print-dialog-container {
  display: flex;
  height: 600px;
}

.print-settings {
  width: 200px;
  background: #f5f5f5;
  padding: 15px;
  border-right: 1px solid #e8e8e8;
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.settings-section {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.settings-section h4 {
  margin: 0;
  font-size: 13px;
  color: #333;
  font-weight: 600;
}

.print-actions {
  margin-top: auto;
  padding-top: 20px;
  border-top: 1px solid #e8e8e8;
}

.print-preview-area {
  flex: 1;
  display: flex;
  flex-direction: column;
  background: #e8e8e8;
}

.preview-toolbar {
  padding: 8px 15px;
  background: #fff;
  border-bottom: 1px solid #e8e8e8;
  font-size: 12px;
  color: #666;
}

.preview-content {
  flex: 1;
  overflow: auto;
  padding: 20px;
  display: flex;
  justify-content: center;
}

.preview-paper {
  background: white;
  min-width: 210mm;
  min-height: 297mm;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  transform-origin: top center;
  padding: 20px;
}
</style>
